import {view, Action, Tooltip, Badge} from '@sail/ui';
import {useIntl} from 'react-intl';
import {noTranslateMessages} from '../helpers/noTranslate';
import {widgetLinkAction} from '../helpers/widgetLinkAction';
import {
  useWidgetConversationId,
  useWidgetRoutingMode,
} from '../Widget/useWidget';
import {getServiceEnvironment} from '../utils/getServiceEnvironment';
import {getAdminBaseUrl} from '../utils/getAdminBaseUrl';

const CONVERSATION_PATH = '/universal-help/conversation-details/v2/';

const devMessages = noTranslateMessages({
  badgeLabel: {
    id: 'conversations_ui.header.dev_badge_label',
    defaultMessage: 'Dev',
  },
  normalRoutingModeLabel: {
    id: 'support_conversations_client.header.dev_badge.normal_routing_mode_label',
    defaultMessage: 'Routing mode: normal',
  },
  testQueueRoutingModeLabel: {
    id: 'support_conversations_client.header.dev_badge.test_queue_routing_mode_label',
    defaultMessage: 'Routing mode: test queue',
  },
  syntheticsModeLabel: {
    id: 'support_conversations_client.header.dev_badge.synthetics_mode_label',
    defaultMessage: 'Synthetics',
  },
  conversationTranscriptLabel: {
    id: 'support_conversations_client.header.dev_badge.conversation_transcript_label',
    defaultMessage: 'Transcript',
  },
  forestVersionLabel: {
    id: 'support_conversations_client.header.dev_badge.forest_version_label',
    defaultMessage: 'Forest version: {forestVersion}',
  },
  testAgentModeLabel: {
    id: 'support_conversations_client.header.dev_badge.test_agent_mode_label',
    defaultMessage: 'Test agent mode: {testAgentMode}',
  },
});
function showDevBadge(
  routingMode: string | undefined,
  forestVersion: string,
  testAgentMode: string,
) {
  const env = getServiceEnvironment();
  if (
    env === 'devbox' ||
    env === 'qa' ||
    env === 'isolated_qa' ||
    forestVersion !== 'off' ||
    testAgentMode !== 'off' ||
    routingMode === 'dev'
  ) {
    return true;
  }

  return false;
}

function generateTranscriptLink(conversationId?: string) {
  const adminBaseUrl = getAdminBaseUrl();
  return `${adminBaseUrl}${CONVERSATION_PATH}${conversationId}`;
}

export function DevBadge() {
  const {conversationId} = useWidgetConversationId();
  const {routingMode} = useWidgetRoutingMode();
  const intl = useIntl();
  const isTestQueue = routingMode === 'dev' || routingMode === 'synthetic';
  const urlSearchParams = new URLSearchParams(window.location.search);
  const forestVersion = urlSearchParams.get('forestVersion') || 'off';
  const testAgentMode = urlSearchParams.get('test_agent_mode') || 'off';

  if (!showDevBadge(routingMode, forestVersion, testAgentMode)) {
    if (routingMode === 'synthetic') {
      return (
        <Badge type="info" data-testid="dev-badge">
          {intl.formatMessage(devMessages.syntheticsModeLabel)}
        </Badge>
      );
    }

    return null;
  }

  const transcriptLink = generateTranscriptLink(conversationId);

  return (
    <Tooltip
      trigger={
        <Badge type="info" data-testid="dev-badge">
          {intl.formatMessage(devMessages.badgeLabel)}
        </Badge>
      }
    >
      <view.div css={{stack: 'y', gap: 'small'}}>
        {conversationId ? (
          <Action href={transcriptLink} uses={[widgetLinkAction()]}>
            {intl.formatMessage(devMessages.conversationTranscriptLabel)}
          </Action>
        ) : null}
        <view.div>
          {routingMode === 'normal'
            ? intl.formatMessage(devMessages.normalRoutingModeLabel)
            : null}
          {isTestQueue
            ? intl.formatMessage(devMessages.testQueueRoutingModeLabel)
            : null}
        </view.div>
        <view.div>
          {intl.formatMessage(devMessages.forestVersionLabel, {
            forestVersion,
          })}
        </view.div>
        <view.div>
          {intl.formatMessage(devMessages.testAgentModeLabel, {
            testAgentMode,
          })}
        </view.div>
      </view.div>
    </Tooltip>
  );
}
